// Snowflake ID Generator Implementation
// Generates Twitter or Discord format Snowflake IDs
// References:
// - Discord: https://discord.com/developers/docs/reference#snowflakes
// - Twitter: https://developer.twitter.com/en/docs/twitter-ids

// Twitter epoch: November 4, 2010, 01:42:54 UTC
const TWITTER_EPOCH = 1288834974657n;

// Discord epoch: January 1, 2015, 00:00:00 UTC
const DISCORD_EPOCH = 1420070400000n;

/**
 * Generates a new Snowflake ID in Twitter or Discord format
 *
 * Snowflake ID structure (64 bits):
 * - 1 bit: unused (always 0)
 * - 41 bits: timestamp (milliseconds since platform epoch)
 * - 10 bits: machine ID (5 bits datacenter + 5 bits worker)
 * - 12 bits: sequence number
 *
 * @param platform - Platform to generate for ('Twitter' or 'Discord'), defaults to 'Discord'
 * @param datacenterId - Optional datacenter ID (0-31), defaults to random
 * @param workerId - Optional worker ID (0-31), defaults to random
 * @param sequence - Optional sequence number (0-4095), defaults to random
 */
export function generateSnowflake(
  platform: 'Twitter' | 'Discord' = 'Discord',
  datacenterId?: number,
  workerId?: number,
  sequence?: number
): string {
  const timestamp = BigInt(Date.now());
  const epoch = platform === 'Twitter' ? TWITTER_EPOCH : DISCORD_EPOCH;
  const timestampOffset = timestamp - epoch;

  // Generate random values if not provided
  const randomBytes = new Uint8Array(2);
  crypto.getRandomValues(randomBytes);

  const finalDatacenterId = datacenterId !== undefined
    ? BigInt(datacenterId & 0b11111)
    : BigInt(randomBytes[0] & 0b11111);

  const finalWorkerId = workerId !== undefined
    ? BigInt(workerId & 0b11111)
    : BigInt(randomBytes[1] & 0b11111);

  const finalSequence = sequence !== undefined
    ? BigInt(sequence & 0b111111111111)
    : BigInt(Math.floor(Math.random() * 4096));

  // Construct the Snowflake ID
  // timestamp (41 bits) << 22 | datacenter (5 bits) << 17 | worker (5 bits) << 12 | sequence (12 bits)
  const snowflakeId =
    (timestampOffset << 22n) |
    (finalDatacenterId << 17n) |
    (finalWorkerId << 12n) |
    finalSequence;

  return snowflakeId.toString();
}
